/**
 * Transforms workflow run data into WaterfallChart entries.
 *
 * @module FlowDesigner/runs/useWaterfallData
 */

import { useMemo } from 'react';
import {
  formatDurationLabel,
  type WaterfallEntry,
  type WaterfallSubEntry,
  type WaterfallBarTone,
} from '@chatsdet/ui';
import type {
  WorkflowRunWithStepsDto,
  WorkflowRunStepDto,
  WorkflowRunStepMetadataDto,
} from '../hooks/useWorkflowRun';
import type { WorkflowRunThoughtDto } from '../hooks/useWorkflowRunThoughts';
import type { WorkflowNode } from '../../../../types/workflow';
import { nodeLabel } from '../utils/nodeLabel';
import { getStepCacheBadge } from './stepCacheBadge';

type WaterfallData = {
  entries: WaterfallEntry[];
  totalDurationMs: number;
};

export function stepTone(step: WorkflowRunStepDto): WaterfallBarTone {
  switch (step.status) {
    case 'failed':
      return 'danger';
    case 'inconclusive':
      return 'muted';
    case 'running':
      return 'warning';
    case 'waiting':
    case 'skipped':
    case 'pending':
      return 'muted';
    case 'success': {
      const cacheOutcome = step.metadataJson?.cacheOutcome;
      if (cacheOutcome === 'hit') return 'cached';
      if (cacheOutcome === 'miss' || cacheOutcome === 'fallback') return 'agent';
      return 'success';
    }
  }
}

/** Build sub-entries from turnStats (precise per-turn timing from agent). */
export function buildTurnSubEntries(
  stepId: string,
  turnStats: NonNullable<WorkflowRunStepMetadataDto['turnStats']>,
  stepStartMs: number
): WaterfallSubEntry[] {
  const entries: WaterfallSubEntry[] = [];

  for (const turn of turnStats.turns) {
    const durationLabel = formatDurationLabel(turn.durationMs);
    const ttftFormatted =
      turn.llmFirstResponseMs !== null && turn.llmFirstResponseMs !== undefined
        ? formatDurationLabel(turn.llmFirstResponseMs)
        : null;

    // Short inline label
    const label = `Turn ${turn.turnNumber} · ${durationLabel}`;

    // Detailed tooltip
    const details = [
      `Duration: ${durationLabel}`,
      ttftFormatted ? `Time to first token: ${ttftFormatted}` : null,
      `Input tokens: ${turn.inputTokens.toLocaleString()}`,
      `Output tokens: ${turn.outputTokens.toLocaleString()}`,
      `Finish reason: ${turn.finishReason}`,
    ]
      .filter(Boolean)
      .join('\n');

    entries.push({
      id: `${stepId}-turn-${turn.turnNumber}`,
      label,
      tooltipDetail: details,
      startMs: Math.max(0, turn.startedAtMs - stepStartMs),
      durationMs: Math.max(100, turn.durationMs),
      tone: 'agent',
      type: 'llm-turn',
    });
  }

  for (let i = 0; i < turnStats.retries.errors.length; i++) {
    const error = turnStats.retries.errors[i]!;
    entries.push({
      id: `${stepId}-retry-${i}-${error.timestampMs}`,
      label: `Retry: ${error.type}`,
      tooltipDetail: error.message,
      startMs: Math.max(0, error.timestampMs - stepStartMs),
      durationMs: 200,
      tone: 'danger',
      type: 'retry-error',
    });
  }

  return entries;
}

/** Build sub-entries from thoughts (fallback for runs without turnStats). */
const thoughtTimestamp = (t: WorkflowRunThoughtDto) =>
  new Date(t.startedAt ?? t.createdAt).getTime();

export function buildThoughtSubEntries(
  stepThoughts: WorkflowRunThoughtDto[],
  stepStartMs: number
): WaterfallSubEntry[] {
  if (stepThoughts.length === 0) return [];

  const sorted = [...stepThoughts].sort((a, b) => thoughtTimestamp(a) - thoughtTimestamp(b));

  return sorted.map((thought, i) => {
    const time = thoughtTimestamp(thought);
    const nextTime = i + 1 < sorted.length ? thoughtTimestamp(sorted[i + 1]!) : time + 500;
    const isToolCall = !!thought.toolName;

    return {
      id: thought.id,
      label: thought.toolName ?? 'Thinking',
      startMs: Math.max(0, time - stepStartMs),
      durationMs: Math.max(100, nextTime - time),
      tone: isToolCall ? 'agent' : 'cached',
      type: isToolCall ? 'tool-call' : 'thought',
    };
  });
}

export function buildErrorMessage(step: WorkflowRunStepDto): string | undefined {
  if (step.status !== 'failed') return undefined;
  const parts: string[] = [];
  if (step.errorMessage) parts.push(step.errorMessage);
  const ctx = step.metadataJson?.failureContext;
  if (ctx?.expectedBehavior) parts.push(`Expected: ${ctx.expectedBehavior}`);
  if (ctx?.observedBehavior) parts.push(`Observed: ${ctx.observedBehavior}`);
  return parts.length > 0 ? parts.join('\n') : undefined;
}

/** Pure transformation — extracted so it's testable without React. */
export function computeWaterfallData(
  run: WorkflowRunWithStepsDto | null | undefined,
  thoughts: WorkflowRunThoughtDto[] | null | undefined,
  nodes: WorkflowNode[]
): WaterfallData {
  if (!run?.startedAt) return { entries: [], totalDurationMs: 0 };

  const nowMs = Date.now();
  const runStartMs = new Date(run.startedAt).getTime();
  const runEndMs = run.finishedAt ? new Date(run.finishedAt).getTime() : nowMs;
  const totalDurationMs = Math.max(1, runEndMs - runStartMs);

  // Group thoughts by stepId (fallback for steps without turnStats)
  const thoughtsByStep = new Map<string, WorkflowRunThoughtDto[]>();
  for (const thought of thoughts ?? []) {
    if (!thought.stepId) continue;
    const existing = thoughtsByStep.get(thought.stepId) ?? [];
    existing.push(thought);
    thoughtsByStep.set(thought.stepId, existing);
  }

  const entries: WaterfallEntry[] = run.steps.map((step) => {
    const stepStartMs = step.startedAt ? new Date(step.startedAt).getTime() : null;
    const stepEndMs = step.finishedAt
      ? new Date(step.finishedAt).getTime()
      : step.status === 'running'
        ? nowMs
        : null;

    const startMs = stepStartMs ? stepStartMs - runStartMs : 0;
    const durationMs = stepStartMs && stepEndMs ? Math.max(0, stepEndMs - stepStartMs) : 0;

    const cacheBadge = getStepCacheBadge(step.metadataJson ?? null);
    const badges = cacheBadge ? [{ label: cacheBadge.label, tone: cacheBadge.tone }] : [];

    // Prefer turnStats (precise) over thoughts (approximate)
    let subEntries: WaterfallSubEntry[] = [];
    if (stepStartMs && durationMs > 0) {
      const turnStats = step.metadataJson?.turnStats;
      if (turnStats && turnStats.turns.length > 0) {
        subEntries = buildTurnSubEntries(step.id, turnStats, stepStartMs);
      } else {
        const stepThoughts = thoughtsByStep.get(step.id) ?? [];
        if (stepThoughts.length > 0) {
          subEntries = buildThoughtSubEntries(stepThoughts, stepStartMs);
        }
      }
    }

    return {
      id: step.id,
      label: nodeLabel(nodes, step.nodeId),
      sublabel: nodes.find((n) => n.id === step.nodeId)?.type,
      startMs,
      durationMs,
      status: step.status,
      tone: stepTone(step),
      badges,
      subEntries,
      errorMessage: buildErrorMessage(step),
    };
  });

  return { entries, totalDurationMs };
}

export function useWaterfallData(
  run: WorkflowRunWithStepsDto | null | undefined,
  thoughts: WorkflowRunThoughtDto[] | null | undefined,
  nodes: WorkflowNode[]
): WaterfallData {
  return useMemo(() => computeWaterfallData(run, thoughts, nodes), [run, thoughts, nodes]);
}
